import { useEffect, useState, useTransition } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Handshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useDict, fill } from "@/lib/i18n/context";
import { resolveApiError } from "@/lib/api-error";

type Preview = {
  invited_email: string;
  status: string;
  client_company_name: string;
  token: string;
};

export function PartnershipAcceptPage({
  onAuth,
}: {
  onAuth?: () => void;
}) {
  const { token } = useParams<{ token: string }>();
  const t = useDict();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [userEmail, setUserEmail] = useState<string | null | undefined>(
    undefined,
  );
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [pending, startTransition] = useTransition();

  useEffect(() => {
    if (!token) return;
    fetch(`/api/partnerships/invite/${token}`, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) throw new Error("not found");
        return res.json() as Promise<Preview>;
      })
      .then(setPreview)
      .catch(() => setError(t.errors.inviteNotFound));
  }, [token, t.errors.inviteNotFound]);

  useEffect(() => {
    fetch("/api/auth/me", { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) {
          setUserEmail(null);
          return;
        }
        const data = (await res.json()) as { user: { email: string } };
        setUserEmail(data.user.email);
      })
      .catch(() => setUserEmail(null));
  }, []);

  function onAccept() {
    if (!token) return;
    setError(null);
    startTransition(async () => {
      const res = await fetch("/api/partnerships/accept", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const data = (await res.json()) as { error?: string; code?: string };
      if (!res.ok || data.error) {
        setError(resolveApiError(t, data, "inviteAcceptFailed"));
        return;
      }
      setSuccess(true);
      onAuth?.();
    });
  }

  if (error && !preview) {
    return (
      <div className="mx-auto flex min-h-[50vh] max-w-lg items-center justify-center p-6">
        <Card>
          <CardContent className="space-y-3 pt-6 text-center">
            <p className="font-medium">{error}</p>
            <Link to="/dashboard" className="text-brand-600">
              {t.common.back}
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!preview || userEmail === undefined) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted">
        Loading…
      </div>
    );
  }

  if (success) {
    return (
      <div className="mx-auto flex min-h-[50vh] max-w-lg items-center justify-center p-6">
        <Card>
          <CardContent className="space-y-4 pt-6 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <Handshake className="h-7 w-7" />
            </div>
            <p className="font-medium">{t.partners.acceptSuccess}</p>
            <Button onClick={() => navigate("/partners")}>
              {t.partners.title}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const redirect = `/partnerships/accept/${token}`;
  const emailMatch =
    userEmail &&
    userEmail.trim().toLowerCase() ===
      preview.invited_email.trim().toLowerCase();

  return (
    <div className="mx-auto flex min-h-[50vh] max-w-lg items-center justify-center p-6">
      <Card className="w-full">
        <CardContent className="space-y-4 pt-6">
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-100 text-brand-700">
              <Handshake className="h-6 w-6" />
            </span>
            <div>
              <h1 className="text-xl font-bold">{t.partners.acceptTitle}</h1>
              <p className="text-sm text-muted">
                {fill(t.partners.acceptSubtitle, {
                  company: preview.client_company_name,
                })}
              </p>
            </div>
          </div>

          <p className="rounded-lg bg-surface px-3 py-2 text-sm text-muted">
            {fill(t.partners.acceptWrongEmail, {
              email: preview.invited_email,
            })}
          </p>

          {preview.status === "revoked" && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              This invitation was revoked.
            </p>
          )}
          {preview.status === "active" && (
            <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              {t.partners.acceptSuccess}
            </p>
          )}

          {error && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )}

          {preview.status === "pending" && (
            <>
              {!userEmail && (
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Link
                    to={`/sign-in?redirect=${encodeURIComponent(redirect)}`}
                    className="flex-1"
                  >
                    <Button className="w-full" variant="outline">
                      {t.partners.acceptSignIn}
                    </Button>
                  </Link>
                  <Link
                    to={`/sign-up?redirect=${encodeURIComponent(redirect)}&agency=1`}
                    className="flex-1"
                  >
                    <Button className="w-full">
                      {t.partners.acceptSignUp}
                    </Button>
                  </Link>
                </div>
              )}
              {userEmail && !emailMatch && (
                <p className="text-sm text-muted">
                  Signed in as {userEmail}.{" "}
                  <Link
                    to={`/sign-in?redirect=${encodeURIComponent(redirect)}`}
                    className="font-medium text-brand-600"
                  >
                    {t.partners.acceptSignIn}
                  </Link>
                </p>
              )}
              {userEmail && emailMatch && (
                <Button
                  className="w-full"
                  loading={pending}
                  onClick={onAccept}
                >
                  {t.partners.acceptCta}
                </Button>
              )}
            </>
          )}

          {preview.status === "active" && (
            <Button className="w-full" onClick={() => navigate("/partners")}>
              {t.partners.title}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
